<template>
  <div class="headerTrivia">
    <h1 class="list-title">{{ $t('trivia.list.title') }}</h1>
    <v-data-table
      :headers="headers"
      :items="trivias"
      :search="searchTerm"
      :no-data-text="$t('trivia.list.table.noData')"
      :loading="loading"
      class="tw-list-table"
    >
      <template v-slot:top>
        <div class="tw-list-toolbar">
          <v-text-field
            :label="$t('trivia.list.search.label')"
            :placeholder="$t('trivia.list.search.placeholder')"
            append-icon="mdi-magnify"
            dense
            outlined
            hide-details
            class="tw-list-search"
            @input="search"
          ></v-text-field>

          <v-btn color="primary" class="tw-list-add" @click="createTrivia">
            <v-icon left>mdi-plus</v-icon>
            {{ $t('trivia.list.button.new') }}
          </v-btn>
        </div>
      </template>

      <template v-slot:[`item.title`]="{ item }">
        <div class="trivia-title">
          <span class="custom-font">{{ item.title }}</span>
          <span class="trivia-title__created">{{ $t('trivia.list.createdAt', { date: formatDate(item.createdAt) }) }}</span>
        </div>
      </template>

      <template v-slot:[`item.status`]="{ item }">
        <v-chip :class="['status-chip', getStatusClass(item.status)]" small>
          {{ getStatusLabel(item.status) }}
        </v-chip>
      </template>

      <template v-slot:[`item.questionCount`]="{ item }">
        <span class="custom-font">{{ item.questionCount }}</span>
      </template>

      <template v-slot:[`item.sessionCount`]="{ item }">
        <span class="custom-font">{{ item.sessionCount }}</span>
      </template>

      <template v-slot:[`item.createdAt`]="{ item }">
        <span class="custom-font">{{ formatDate(item.createdAt) }}</span>
      </template>

      <template v-slot:[`item.action`]="{ item }">
        <div class="actions">
          <v-btn icon small @click="viewTrivia(item)" :title="$t('trivia.list.action.view')">
            <v-icon color="primary">mdi-eye</v-icon>
          </v-btn>
          <v-btn icon small @click="editTrivia(item)" :title="$t('trivia.list.action.edit')" :disabled="item.status !== 'DRAFT'">
            <v-icon color="primary">mdi-pencil</v-icon>
          </v-btn>
          <v-btn icon small @click="publishTrivia(item)" :title="$t('trivia.list.action.publish')" :disabled="item.status !== 'DRAFT'">
            <v-icon color="primary">mdi-send</v-icon>
          </v-btn>
          <v-btn icon small @click="closeTrivia(item)" :title="$t('trivia.list.action.close')" :disabled="item.status !== 'PUBLISHED'">
            <v-icon color="primary">mdi-stop-circle</v-icon>
          </v-btn>
          <v-btn icon small @click="startLiveSession(item)" :title="$t('trivia.list.action.live')" :disabled="item.status !== 'PUBLISHED' || item.status === 'CLOSED'">
            <v-icon color="success">mdi-play-circle</v-icon>
          </v-btn>
          <v-btn icon small @click="deleteTrivia(item)" :title="$t('trivia.list.action.delete')" :disabled="item.status === 'PUBLISHED'">
            <v-icon color="red">mdi-delete</v-icon>
          </v-btn>
        </div>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { service as TriviaService } from '@/services/trivia_service.js';
import Swal from "sweetalert2";

function getSessionContext() {
    return {
        idGroup: sessionStorage.getItem('idGroup'),
        idEmpresa: sessionStorage.getItem('idEmpresa'),
        idUsuario: sessionStorage.getItem('idUsuario')
    };
}

export default {
  name: "listTriviaComponent",

  data() {
    return {
      searchTerm: "",
      loading: false,
      trivias: []
    };
  },

  created() {
    this.loadTrivias();
  },

  methods: {
    search(value) {
      this.searchTerm = value;
    },

    async loadTrivias() {
      this.loading = true;
      const { idGroup, idEmpresa, idUsuario } = getSessionContext();
      try {
        const resp = await TriviaService.getListTrivias(idGroup, idEmpresa, idUsuario, { page: 0, size: 50 });
        if (resp.data && resp.data.payload && resp.data.payload.content) {
          this.trivias = resp.data.payload.content;
        } else {
          this.trivias = [];
        }
      } catch (err) {
        console.error('Error cargando trivias', err);
        this.trivias = [];
      } finally {
        this.loading = false;
      }
    },

    createTrivia() {
      this.$router.push({ name: 'trivia-create' });
    },

    getItemId(item) {
      return item.triviaId || item.id;
    },

    viewTrivia(item) {
      this.$router.push({ name: 'trivia-create', query: { id: this.getItemId(item), mode: 'view' } });
    },

    editTrivia(item) {
      this.$router.push({ name: 'trivia-create', query: { id: this.getItemId(item), mode: 'edit' } });
    },

    async publishTrivia(item) {
      const { idGroup, idEmpresa, idUsuario } = getSessionContext();
      try {
        this.loading = true;
        await TriviaService.patchTriviaStatus(idGroup, idEmpresa, idUsuario, this.getItemId(item), 'PUBLISHED');
        await this.loadTrivias();
      } catch (err) {
        console.error('Error publicando trivia', err);
        alert(this.$t('trivia.list.alert.publishError'));
      } finally {
        this.loading = false;
      }
    },

    async closeTrivia(item) {
      const result = await Swal.fire({
        title: this.$t('trivia.list.confirm.closeTitle'),
        text: this.$t('trivia.list.confirm.closeText', { title: item.title }),
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t('trivia.list.confirm.closeAccept'),
        cancelButtonText: this.$t('trivia.list.confirm.cancel'),
        confirmButtonColor: '#6E17A5'
      });
      if (!result.isConfirmed) return;

      const { idGroup, idEmpresa, idUsuario } = getSessionContext();
      try {
        this.loading = true;
        await TriviaService.patchTriviaStatus(idGroup, idEmpresa, idUsuario, this.getItemId(item), 'CLOSED');
        await this.loadTrivias();
      } catch (err) {
        console.error('Error cerrando trivia', err);
        alert(this.$t('trivia.list.alert.closeError'));
      } finally {
        this.loading = false;
      }
    },

    async deleteTrivia(item) {
      const result = await Swal.fire({
        title: this.$t('trivia.list.confirm.deleteTitle'),
        text: this.$t('trivia.list.confirm.deleteText', { title: item.title }),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('trivia.list.confirm.deleteAccept'),
        cancelButtonText: this.$t('trivia.list.confirm.cancel'),
        confirmButtonColor: '#c62828'
      });
      if (!result.isConfirmed) return;

      const { idGroup, idEmpresa, idUsuario } = getSessionContext();
      try {
        this.loading = true;
        await TriviaService.deleteTrivia(idGroup, idEmpresa, idUsuario, this.getItemId(item));
        await this.loadTrivias();
      } catch (err) {
        console.error('Error eliminando trivia', err);
        alert(this.$t('trivia.list.alert.deleteError'));
      } finally {
        this.loading = false;
      }
    },

    async startLiveSession(item) {
      if (item.status !== 'PUBLISHED') return;
      const { idGroup, idEmpresa, idUsuario } = getSessionContext();
      try {
        this.loading = true;
        const resp = await TriviaService.postCreateSession(idGroup, idEmpresa, idUsuario, this.getItemId(item));
        if (resp.data && resp.data.payload) {
          const sessionId = resp.data.payload.sessionId || resp.data.payload.id;
          sessionStorage.setItem('trivia_session_' + sessionId, JSON.stringify(resp.data.payload));
          this.$router.push({
            name: 'trivia-lobby',
            params: { sessionId },
            query: { triviaId: this.getItemId(item), triviaTitle: item.title }
          });
        }
      } catch (err) {
        console.error('Error creando sesión', err);
        alert(this.$t('trivia.list.alert.liveError'));
      } finally {
        this.loading = false;
      }
    },

    getStatusClass(status) {
      switch (status) {
        case 'PUBLISHED': return 'status-chip--published';
        case 'DRAFT': return 'status-chip--draft';
        case 'CLOSED': return 'status-chip--closed';
        default: return 'status-chip--draft';
      }
    },

    getStatusLabel(status) {
      const labels = {
        'PUBLISHED': this.$t('trivia.list.status.published'),
        'DRAFT': this.$t('trivia.list.status.draft'),
        'CLOSED': this.$t('trivia.list.status.closed')
      };
      return labels[status] || status;
    },

    formatDate(value) {
      if (!value) return '-';
      return new Date(value).toLocaleDateString(this.$i18n.locale);
    }
  },

  computed: {
    headers() {
      return [
        { text: this.$t('trivia.list.headers.name'), value: 'title', align: 'center', sortable: false, class: 'primary--text custom-header font-weight-medium text-subtitle-1' },
        { text: this.$t('trivia.list.headers.status'), value: 'status', align: 'center', sortable: false, class: 'primary--text custom-header font-weight-medium text-subtitle-1' },
        { text: this.$t('trivia.list.headers.questions'), value: 'questionCount', align: 'center', sortable: false, class: 'primary--text custom-header font-weight-medium text-subtitle-1' },
        { text: this.$t('trivia.list.headers.sessions'), value: 'sessionCount', align: 'center', sortable: false, class: 'primary--text custom-header font-weight-medium text-subtitle-1' },
        { text: this.$t('trivia.list.headers.createdAt'), value: 'createdAt', align: 'center', sortable: false, class: 'primary--text custom-header font-weight-medium text-subtitle-1' },
        { text: this.$t('trivia.list.headers.actions'), value: 'action', align: 'center', sortable: false, class: 'primary--text custom-header font-weight-medium text-subtitle-1' }
      ];
    }
  }
};
</script>

<style lang="css" scoped>
.headerTrivia h1,
.list-title {
  margin-bottom: 30px;
  color: #6E17A5;
}

.headerTrivia {
  display: flex;
  gap: 40px;
  flex-direction: column;
  margin-top: 20px;
  margin-left: 20px;
}

.actions {
  display: flex;
  justify-content: center;
  gap: 4px;
}

.trivia-title {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.trivia-title__created {
  font-size: 12px;
  color: #888;
  margin-top: 2px;
}

.custom-font {
  font-size: 15px;
}

.status-chip {
  font-size: 13px;
  font-weight: 500;
  border-radius: 12px;
  opacity: 0.9;
}

.status-chip--published {
  background-color: rgba(76, 175, 80, 0.15) !important;
  color: #2e7d32 !important;
}

.status-chip--draft {
  background-color: rgba(158, 158, 158, 0.15) !important;
  color: #616161 !important;
}

.status-chip--closed {
  background-color: rgba(244, 67, 54, 0.15) !important;
  color: #c62828 !important;
}

h1, h2, h3, h4, h5, h6 {
  font-family: "Amsi Pro", sans-serif;
  color: #6E17A5;
  font-weight: normal;
}
</style>
